import React, { useEffect, useState } from 'react';
import {
    Badge,
    Button,
    Collapse,
    Divider,
    Flex,
    Grid,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    ThemeIcon,
    Timeline,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
    IconAlertTriangle,
    IconArrowLeft,
    IconArrowUp,
    IconBell,
    IconBolt,
    IconCalendar,
    IconCheck,
    IconCheckbox,
    IconChevronDown,
    IconChevronRight,
    IconClock,
    IconDownload,
    IconMail,
    IconMessage,
    IconNote,
    IconShieldCheck,
    IconUser,
} from '@tabler/icons-react';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import { useAlertDetail } from '../../../global/entities/Alert/queries';
import {
    useAcknowledgeAlert,
    useResolveAlert,
    useAddAlertComment,
    useNotifyPatientFromAlert,
    useNotifyAssistantFromAlert,
    useMarkAlertVisited,
    useDownloadAlertPdf,
    useSendAlertToAssistant,
} from '../../../global/entities/Alert/mutations';
import { AlertSeverity, AlertStatus, IAlertAuditLogItem } from '../../../global/entities/Alert/Alert.ts';
import Utils from '../../../global/helpers/utils.ts';
import { PostHogService } from '../../../global/services/posthogService.ts';

const severityColorMap: Record<string, string> = {
    [AlertSeverity.Low]: 'blue',
    [AlertSeverity.Moderate]: 'yellow',
    [AlertSeverity.High]: 'orange',
};

const statusColorMap: Record<string, string> = {
    [AlertStatus.Open]: 'red',
    [AlertStatus.Acknowledged]: 'teal',
    [AlertStatus.Resolved]: 'green',
    [AlertStatus.Escalated]: 'orange',
};

const RESOLVE_QUICK_MESSAGES = [
    'clinicalIntervention',
    'noActionRequired',
    'falseAlarm',
    'patientContacted',
    'monitoringContinued',
    'other',
] as const;

const NOTIFY_QUICK_MESSAGES = [
    'contactClinic',
    'seekEmergency',
    'monitorSymptoms',
    'symptomsWorsen',
    'teamWillContact',
    'noActionRequired',
    'other',
] as const;

const NOTIFY_ASSISTANT_QUICK_MESSAGES = [
    'contactPatient',
    'scheduleFollowUp',
    'reviewAlert',
    'verifyInfo',
    'repeatMeasurement',
    'documentFollowUp',
    'escalateClinician',
    'other',
] as const;

const auditActionColor = (action: string): string => {
    switch (action) {
        case 'Triggered': return 'red';
        case 'Acknowledged': return 'green';
        case 'Resolved': return 'teal';
        case 'Commented': return 'blue';
        case 'PatientNotified': return 'cyan';
        case 'AssistantNotified': return 'grape';
        case 'Escalated': return 'orange';
        default: return 'gray';
    }
};

const auditActionIcon = (action: string): React.ReactNode => {
    switch (action) {
        case 'Triggered': return <IconBolt size={12} />;
        case 'Acknowledged': return <IconCheck size={12} />;
        case 'Resolved': return <IconShieldCheck size={12} />;
        case 'Commented': return <IconMessage size={12} />;
        case 'PatientNotified': return <IconMail size={12} />;
        case 'AssistantNotified': return <IconMail size={12} />;
        case 'Escalated': return <IconArrowUp size={12} />;
        default: return <IconClock size={12} />;
    }
};

const str = (val: unknown): string => String(val);

const AuditEntryDetail: React.FC<{ entry: IAlertAuditLogItem; t: (key: string, options?: any) => string }> = ({ entry, t }) => {
    const details = entry.details as Record<string, unknown> | null;
    if (!details) return null;

    switch (entry.action) {
        case 'Triggered':
            return (
                <Stack gap={2}>
                    {details.severity && <Text size="xs">{t('alert.table.severity')}: {str(details.severity)}</Text>}
                    {details.triggerValue && <Text size="xs">{t('alert.detail.triggerValue')}: {str(details.triggerValue)}</Text>}
                    {details.sourceType && <Text size="xs">{t('alert.detail.sourceType')}: {str(details.sourceType)}</Text>}
                </Stack>
            );
        case 'Acknowledged':
            return details.reason ? (
                <Text size="xs" fs="italic">{str(details.reason)}</Text>
            ) : null;
        case 'Escalated':
            return (
                <Stack gap={2}>
                    {details.escalationLevel && <Text size="xs">{t('alert.audit.escalationLevel')}: {str(details.escalationLevel)}</Text>}
                    {details.targetName && <Text size="xs">{t('alert.audit.escalationTarget')}: {str(details.targetName)}</Text>}
                </Stack>
            );
        case 'Commented':
            return details.commentId ? (
                <Text size="xs" c="dimmed">{t('alert.audit.commentRef', { id: str(details.commentId) })}</Text>
            ) : null;
        case 'PatientNotified':
            return details.deliveryChannel ? (
                <Text size="xs">{t('alert.audit.channel')}: {str(details.deliveryChannel)}</Text>
            ) : null;
        case 'Resolved':
            return (
                <Stack gap={2}>
                    {details.previousStatus && <Text size="xs">{t('alert.audit.previousStatus')}: {str(details.previousStatus)}</Text>}
                    {details.hasNotes && <Text size="xs" fs="italic">{t('alert.audit.hasResolutionNotes')}</Text>}
                </Stack>
            );
        default:
            return null;
    }
};

interface InfoFieldProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    color: string;
}

const InfoField: React.FC<InfoFieldProps> = ({ icon, label, value, color }) => (
    <Group gap="sm" wrap="nowrap">
        <ThemeIcon size="md" variant="light" color={color} radius="xl">
            {icon}
        </ThemeIcon>
        <Stack gap={2}>
            <Text size="xs" c="dimmed" fw={500}>
                {label}
            </Text>
            <Text size="sm" fw={500}>
                {value}
            </Text>
        </Stack>
    </Group>
);

const AlertDetailForm: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const alertId = id ? parseInt(id, 10) : undefined;

    const [resolutionNotes, setResolutionNotes] = useState('');
    const [commentText, setCommentText] = useState('');
    const [notifyMessage, setNotifyMessage] = useState('');
    const [assistantMessage, setAssistantMessage] = useState('');
    const [expandedAuditIds, setExpandedAuditIds] = useState<number[]>([]);
    const [sendToAssistantModalOpen, setSendToAssistantModalOpen] = useState(false);
    const [noAssistantModalOpen, setNoAssistantModalOpen] = useState(false);
    const [patientHistoryOpen, setPatientHistoryOpen] = useState(false);
    const [assistantHistoryOpen, setAssistantHistoryOpen] = useState(false);

    const resolveTextareaRef = React.useRef<HTMLTextAreaElement>(null);
    const notifyTextareaRef = React.useRef<HTMLTextAreaElement>(null);
    const assistantTextareaRef = React.useRef<HTMLTextAreaElement>(null);

    const { data: alert, isLoading } = useAlertDetail(alertId);
    const acknowledgeAlert = useAcknowledgeAlert();
    const resolveAlert = useResolveAlert();
    const addComment = useAddAlertComment();
    const notifyPatient = useNotifyPatientFromAlert();
    const notifyAssistant = useNotifyAssistantFromAlert();
    const markVisited = useMarkAlertVisited();
    const downloadPdf = useDownloadAlertPdf();
    const sendToAssistant = useSendAlertToAssistant();

    useEffect(() => {
        if (alertId && markVisited.isIdle) markVisited.mutate(alertId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alertId]);

    if (isLoading || !alert)
        return <Loading visible={true} loadingMessage={'Loading...'} />;

    const title = i18n.language === 'fr' ? alert.titleFr : alert.titleEn;
    const description = i18n.language === 'fr' ? alert.descriptionFr : alert.descriptionEn;

    const handleAcknowledge = () => {
        acknowledgeAlert.mutate(alert.id, {
            onSuccess: () => {
                PostHogService.capture('alert acknowledged', {
                    alert_id: alert.id,
                    alert_severity: alert.severity,
                    source_type: alert.sourceType,
                });
            },
        });
    };

    const handleResolve = () => {
        if (resolutionNotes.trim()) {
            resolveAlert.mutate(
                { id: alert.id, notes: resolutionNotes },
                {
                    onSuccess: () => {
                        setResolutionNotes('');
                        PostHogService.capture('alert resolved', {
                            alert_id: alert.id,
                            alert_severity: alert.severity,
                            source_type: alert.sourceType,
                        });
                        if (alert.assistantEmail)
                            setSendToAssistantModalOpen(true);
                    },
                },
            );
        }
    };

    const handleSendToAssistant = () => {
        if (!alert.assistantEmail) {
            setNoAssistantModalOpen(true);
            return;
        }
        sendToAssistant.mutate({ id: alert.id, language: i18n.language });
    };

    const handleAddComment = () => {
        if (commentText.trim()) {
            addComment.mutate(
                { id: alert.id, comment: commentText },
                { onSuccess: () => setCommentText('') },
            );
        }
    };

    const handleNotifyPatient = () => {
        if (notifyMessage.trim()) {
            notifyPatient.mutate(
                { id: alert.id, message: notifyMessage },
                {
                    onSuccess: () => {
                        setNotifyMessage('');
                        PostHogService.capture('patient notified from alert', {
                            alert_id: alert.id,
                            alert_severity: alert.severity,
                        });
                    },
                },
            );
        }
    };

    const handleNotifyAssistant = () => {
        if (!alert.assistantEmail) {
            setNoAssistantModalOpen(true);
            return;
        }
        if (assistantMessage.trim()) {
            notifyAssistant.mutate(
                { id: alert.id, message: assistantMessage },
                {
                    onSuccess: () => {
                        setAssistantMessage('');
                        PostHogService.capture('assistant notified from alert', {
                            alert_id: alert.id,
                            alert_severity: alert.severity,
                        });
                    },
                },
            );
        }
    };

    const isOpen = alert.status === AlertStatus.Open;
    const isEscalated = alert.status === AlertStatus.Escalated;
    const isAcknowledged = alert.status === AlertStatus.Acknowledged;
    const isResolved = alert.status === AlertStatus.Resolved;

    return (
        <Stack gap="lg" pt="md" pb="xl">
            {/* Header */}
            <Group justify="space-between" align="flex-start">
                <Button
                    variant="subtle"
                    leftSection={<IconArrowLeft size={16} />}
                    onClick={() => navigate('../alert')}
                    color="gray"
                >
                    {t('button.close')}
                </Button>
                <Group gap="xs">
                    <Badge
                        color={severityColorMap[alert.severity] || 'gray'}
                        variant="filled"
                        size="lg"
                    >
                        {t(`alert.severity.${alert.severity.toLowerCase()}`)}
                    </Badge>
                    <Badge
                        color={statusColorMap[alert.status] || 'gray'}
                        variant="light"
                        size="lg"
                    >
                        {t(`alert.status.${alert.status.toLowerCase()}`)}
                    </Badge>
                </Group>
            </Group>

            {/* Title */}
            <Title order={2}>{title}</Title>

            <Grid gutter="lg">
                {/* Left column - Details & Comments */}
                <Grid.Col span={{ base: 12, md: 7 }}>
                    <Stack gap="lg">
                        {/* Alert Information */}
                        <Paper p="lg" withBorder radius="md">
                            <Title order={5} mb={description ? 'xs' : 'md'}>
                                {t('alert.detail.alertInfo')}
                            </Title>
                            {description && (
                                <Text size="sm"  mb="md">
                                    {description}
                                </Text>
                            )}
                            
                            <Divider mb="md" />
                            
                            {/* Patient Info */}
                            <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" mb="md">
                                {alert.patientName && (
                                    <InfoField
                                        icon={<IconUser size={14} />}
                                        label={t('alert.table.patient')}
                                        value={alert.patientName}
                                        color="blue"
                                    />
                                )}
                                {alert.patientDateOfBirth && (
                                    <InfoField
                                        icon={<IconCalendar size={14} />}
                                        label={t('alert.detail.patientDob')}
                                        value={Utils.formatDateToYYYYMMDD(alert.patientDateOfBirth!) || ''}
                                        color="blue"
                                    />
                                )}
                                {alert.primaryHcpName && (
                                    <InfoField
                                        icon={<IconUser size={14} />}
                                        label={t('alert.detail.primaryHcp')}
                                        value={alert.primaryHcpName}
                                        color="teal"
                                    />
                                )}
                            </SimpleGrid>

                            <Divider mb="md" />

                            {/* Dates: Triggered → Acknowledged → Resolved */}
                            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                                <InfoField
                                    icon={<IconCalendar size={14} />}
                                    label={t('alert.detail.triggeredDate')}
                                    value={`${Utils.formatLocalDateToYYYYMMDD(alert.triggeredDate)} ${Utils.formatLocalDateToTime(alert.triggeredDate)}`}
                                    color="red"
                                />
                                {/* Source → Trigger Value */}
                                <InfoField
                                    icon={<IconAlertTriangle size={14} />}
                                    label={t('alert.detail.sourceType')}
                                    value={alert.sourceType}
                                    color="gray"
                                />
                                {alert.acknowledgedDate && (
                                    <InfoField
                                        icon={<IconCheck size={14} />}
                                        label={t('alert.detail.acknowledgedDate')}
                                        value={`${Utils.formatLocalDateToYYYYMMDD(alert.acknowledgedDate)} ${Utils.formatLocalDateToTime(alert.acknowledgedDate)}`}
                                        color="yellow"
                                    />
                                )}
                                {alert.triggerValue && (
                                    <InfoField
                                        icon={<IconClock size={14} />}
                                        label={t('alert.detail.triggerValue')}
                                        value={alert.triggerValue}
                                        color="orange"
                                    />
                                )}
                                {alert.resolvedDate && (
                                    <InfoField
                                        icon={<IconCheckbox size={14} />}
                                        label={t('alert.detail.resolvedDate')}
                                        value={`${Utils.formatLocalDateToYYYYMMDD(alert.resolvedDate)} ${Utils.formatLocalDateToTime(alert.resolvedDate)}`}
                                        color="green"
                                    />
                                )}
                            </SimpleGrid>

                            <Divider my="md" />
                            <Flex justify="flex-end" gap="sm">
                                {alert.sourceType === 'Questionnaire' && alert.sourceEntityID && (
                                    <Button
                                        variant="light"
                                        color="cyan"
                                        onClick={() => navigate(`../questionnaire?sourceEntityID=${alert.sourceEntityID}`)}
                                    >
                                        {t('alert.actions.viewQuestionnaire')}
                                    </Button>
                                )}
                                <Button
                                    variant="light"
                                    leftSection={<IconDownload size={16} />}
                                    onClick={() => downloadPdf.mutate({
                                        id: alert.id,
                                        language: i18n.language,
                                        patientName: alert.patientName,
                                        patientDateOfBirth: alert.patientDateOfBirth,
                                        status: alert.status,
                                    })}
                                    loading={downloadPdf.isPending}
                                >
                                    {t('alert.actions.downloadPdf')}
                                </Button>
                                <Button
                                    variant="light"
                                    color="grape"
                                    leftSection={<IconMail size={16} />}
                                    onClick={handleSendToAssistant}
                                    loading={sendToAssistant.isPending}
                                >
                                    {t('alert.actions.sendToAssistant')}
                                </Button>
                            </Flex>
                        </Paper>

                        {/* Resolution Notes */}
                        {alert.resolutionNotes && (
                            <Paper p="lg" withBorder radius="md">
                                <Group gap="xs" mb="sm">
                                    <ThemeIcon
                                        size="sm"
                                        variant="light"
                                        color="green"
                                        radius="xl"
                                    >
                                        <IconNote size={12} />
                                    </ThemeIcon>
                                    <Text size="sm" fw={600}>
                                        {t('alert.detail.resolutionNotes')}
                                    </Text>
                                </Group>
                                <Text
                                    size="sm"
                                    c="dimmed"
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    {alert.resolutionNotes}
                                </Text>
                            </Paper>
                        )}

                        {/* Comments Section */}
                        <Paper p="lg" withBorder radius="md">
                            <Title order={5} mb="md">
                                {t('alert.detail.comments')}
                            </Title>

                            {alert.comments && alert.comments.length > 0 ? (
                                <Timeline
                                    active={alert.comments.length - 1}
                                    bulletSize={28}
                                    lineWidth={2}
                                    color="blue"
                                >
                                    {alert.comments.map((comment) => (
                                        <Timeline.Item
                                            key={comment.id}
                                            bullet={
                                                <ThemeIcon
                                                    size={28}
                                                    radius="xl"
                                                    variant="light"
                                                    color="blue"
                                                >
                                                    <IconMessage size={14} />
                                                </ThemeIcon>
                                            }
                                            title={
                                                <Text size="xs" c="dimmed">
                                                    {Utils.formatLocalDateToYYYYMMDD(
                                                        comment.commentDate,
                                                    )}{' '}
                                                    {Utils.formatLocalDateToTime(comment.commentDate)}
                                                    {comment.userName && ` — ${comment.userName}`}
                                                </Text>
                                            }
                                        >
                                            <Paper p="sm" bg="gray.0" radius="sm" mt={4}>
                                                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{comment.comment}</Text>
                                            </Paper>
                                        </Timeline.Item>
                                    ))}
                                </Timeline>
                            ) : (
                                <Text c="dimmed" size="sm" ta="center" py="md">
                                    {t('alert.detail.noComments')}
                                </Text>
                            )}

                            <Divider my="md" />
                            <Textarea
                                placeholder={t('alert.comment.placeholder')}
                                value={commentText}
                                onChange={(e) =>
                                    setCommentText(e.currentTarget.value)
                                }
                                autosize
                                minRows={3}
                                maxRows={12}
                                maxLength={5000}
                                mb="xs"
                            />
                            <Flex justify="flex-end">
                                <Button
                                    leftSection={<IconMessage size={16} />}
                                    variant="light"
                                    onClick={handleAddComment}
                                    loading={addComment.isPending}
                                    disabled={!commentText.trim()}
                                >
                                    {t('alert.actions.addComment')}
                                </Button>
                            </Flex>
                        </Paper>
                    </Stack>
                </Grid.Col>

                {/* Right column - Actions */}
                <Grid.Col span={{ base: 12, md: 5 }}>
                    <Stack gap="lg">
                        {!isResolved && (
                            <>
                                {/* Acknowledge */}
                                {(isOpen || isEscalated) && (
                                    <Paper p="lg" withBorder radius="md">
                                        <Button
                                            fullWidth
                                            leftSection={<IconCheck size={16} />}
                                            color="teal"
                                            size="md"
                                            onClick={handleAcknowledge}
                                            loading={acknowledgeAlert.isPending}
                                        >
                                            {t('alert.actions.acknowledge')}
                                        </Button>
                                    </Paper>
                                )}

                                {/* Resolve */}
                                {(isOpen || isEscalated || isAcknowledged) && (
                                    <Paper p="lg" withBorder radius="md">
                                        <Title order={5} mb="sm">
                                            {t('alert.resolve.title')}
                                        </Title>
                                        <Select
                                            placeholder={t('alert.quickSelect')}
                                            data={RESOLVE_QUICK_MESSAGES.map((key) => ({
                                                value: key,
                                                label: t(`alert.resolve.quickMessages.${key}`),
                                            }))}
                                            value={null}
                                            onChange={(value) => {
                                                if (value === 'other') {
                                                    setResolutionNotes('');
                                                    resolveTextareaRef.current?.focus();
                                                } else if (value) 
                                                    setResolutionNotes(t(`alert.resolve.quickMessages.${value}`));
                                                
                                            }}
                                            clearable
                                            mb="sm"
                                        />
                                        <Textarea
                                            ref={resolveTextareaRef}
                                            placeholder={t('alert.resolve.notesPlaceholder')}
                                            value={resolutionNotes}
                                            onChange={(e) =>
                                                setResolutionNotes(e.currentTarget.value)
                                            }
                                            autosize
                                            minRows={3}
                                            maxRows={12}
                                            maxLength={5000}
                                            mb="sm"
                                        />
                                        <Button
                                            fullWidth
                                            leftSection={<IconCheckbox size={16} />}
                                            color="green"
                                            onClick={handleResolve}
                                            loading={resolveAlert.isPending}
                                            disabled={!resolutionNotes.trim()}
                                        >
                                            {t('alert.actions.resolve')}
                                        </Button>
                                    </Paper>
                                )}

                                {/* Notify Patient */}
                                <Paper p="lg" withBorder radius="md">
                                    <Title order={5} mb="sm">
                                        {t('alert.notify.title')}
                                    </Title>
                                    <Select
                                        placeholder={t('alert.quickSelect')}
                                        data={NOTIFY_QUICK_MESSAGES.map((key) => ({
                                            value: key,
                                            label: t(`alert.notify.quickMessages.${key}`),
                                        }))}
                                        value={null}
                                        onChange={(value) => {
                                            if (value === 'other') {
                                                setNotifyMessage('');
                                                notifyTextareaRef.current?.focus();
                                            } else if (value) 
                                                setNotifyMessage(t(`alert.notify.quickMessages.${value}`));
                                            
                                        }}
                                        clearable
                                        mb="sm"
                                    />
                                    <Textarea
                                        ref={notifyTextareaRef}
                                        placeholder={t('alert.notify.messagePlaceholder')}
                                        value={notifyMessage}
                                        onChange={(e) =>
                                            setNotifyMessage(e.currentTarget.value)
                                        }
                                        autosize
                                        minRows={3}
                                        maxRows={12}
                                        maxLength={5000}
                                        mb="sm"
                                    />
                                    <Button
                                        fullWidth
                                        leftSection={<IconBell size={16} />}
                                        color="blue"
                                        variant="light"
                                        onClick={handleNotifyPatient}
                                        loading={notifyPatient.isPending}
                                        disabled={!notifyMessage.trim()}
                                    >
                                        {t('alert.actions.notifyPatient')}
                                    </Button>

                                    {/* Notification History */}
                                    {alert.patientNotifications && alert.patientNotifications.length > 0 && (
                                        <>
                                            <Divider my="md" />
                                            <UnstyledButton onClick={() => setPatientHistoryOpen((v) => !v)} w="100%">
                                                <Group gap={4}>
                                                    {patientHistoryOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                                    <Text size="sm" fw={600}>
                                                        {t('alert.notify.history')} ({alert.patientNotifications.length})
                                                    </Text>
                                                </Group>
                                            </UnstyledButton>
                                            <Collapse in={patientHistoryOpen}>
                                                <Timeline
                                                    active={alert.patientNotifications.length - 1}
                                                    bulletSize={28}
                                                    lineWidth={2}
                                                    color="cyan"
                                                    mt="sm"
                                                >
                                                    {alert.patientNotifications.map((notif) => (
                                                        <Timeline.Item
                                                            key={notif.id}
                                                            bullet={
                                                                <ThemeIcon
                                                                    size={28}
                                                                    radius="xl"
                                                                    variant="light"
                                                                    color="cyan"
                                                                >
                                                                    <IconBell size={14} />
                                                                </ThemeIcon>
                                                            }
                                                            title={
                                                                <Text size="xs" c="dimmed">
                                                                    {Utils.formatLocalDateToYYYYMMDD(notif.sentDate)}{' '}
                                                                    {Utils.formatLocalDateToTime(notif.sentDate)}
                                                                    {notif.sentByUserName && ` — ${notif.sentByUserName}`}
                                                                </Text>
                                                            }
                                                        >
                                                            <Paper p="sm" bg="gray.0" radius="sm" mt={4}>
                                                                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notif.message}</Text>
                                                            </Paper>
                                                        </Timeline.Item>
                                                    ))}
                                                </Timeline>
                                            </Collapse>
                                        </>
                                    )}
                                </Paper>

                                {/* Notify Delegated Assistant */}
                                <Paper p="lg" withBorder radius="md">
                                    <Title order={5} mb="sm">
                                        {t('alert.notifyAssistant.title')}
                                    </Title>
                                    <Select
                                        placeholder={t('alert.quickSelect')}
                                        data={NOTIFY_ASSISTANT_QUICK_MESSAGES.map((key) => ({
                                            value: key,
                                            label: t(`alert.notifyAssistant.quickMessages.${key}`),
                                        }))}
                                        value={null}
                                        onChange={(value) => {
                                            if (value === 'other') {
                                                setAssistantMessage('');
                                                assistantTextareaRef.current?.focus();
                                            } else if (value) 
                                                setAssistantMessage(t(`alert.notifyAssistant.quickMessages.${value}`));
                                            
                                        }}
                                        clearable
                                        mb="sm"
                                    />
                                    <Textarea
                                        ref={assistantTextareaRef}
                                        placeholder={t('alert.notifyAssistant.messagePlaceholder')}
                                        value={assistantMessage}
                                        onChange={(e) =>
                                            setAssistantMessage(e.currentTarget.value)
                                        }
                                        autosize
                                        minRows={3}
                                        maxRows={12}
                                        maxLength={5000}
                                        mb="sm"
                                    />
                                    <Button
                                        fullWidth
                                        leftSection={<IconMail size={16} />}
                                        color="grape"
                                        variant="light"
                                        onClick={handleNotifyAssistant}
                                        loading={notifyAssistant.isPending}
                                        disabled={!assistantMessage.trim()}
                                    >
                                        {t('alert.notifyAssistant.title')}
                                    </Button>

                                    {/* Assistant Notification History */}
                                    {alert.assistantNotifications && alert.assistantNotifications.length > 0 && (
                                        <>
                                            <Divider my="md" />
                                            <UnstyledButton onClick={() => setAssistantHistoryOpen((v) => !v)} w="100%">
                                                <Group gap={4}>
                                                    {assistantHistoryOpen ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
                                                    <Text size="sm" fw={600}>
                                                        {t('alert.notifyAssistant.history')} ({alert.assistantNotifications.length})
                                                    </Text>
                                                </Group>
                                            </UnstyledButton>
                                            <Collapse in={assistantHistoryOpen}>
                                                <Timeline
                                                    active={alert.assistantNotifications.length - 1}
                                                    bulletSize={28}
                                                    lineWidth={2}
                                                    color="grape"
                                                    mt="sm"
                                                >
                                                    {alert.assistantNotifications.map((notif) => (
                                                        <Timeline.Item
                                                            key={notif.id}
                                                            bullet={
                                                                <ThemeIcon
                                                                    size={28}
                                                                    radius="xl"
                                                                    variant="light"
                                                                    color="grape"
                                                                >
                                                                    <IconMail size={14} />
                                                                </ThemeIcon>
                                                            }
                                                            title={
                                                                <Text size="xs" c="dimmed">
                                                                    {Utils.formatLocalDateToYYYYMMDD(notif.sentDate)}{' '}
                                                                    {Utils.formatLocalDateToTime(notif.sentDate)}
                                                                    {notif.sentByUserName && ` — ${notif.sentByUserName}`}
                                                                </Text>
                                                            }
                                                        >
                                                            <Paper p="sm" bg="gray.0" radius="sm" mt={4}>
                                                                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{notif.message}</Text>
                                                            </Paper>
                                                        </Timeline.Item>
                                                    ))}
                                                </Timeline>
                                            </Collapse>
                                        </>
                                    )}
                                </Paper>
                            </>
                        )}

                        {/* Resolved status summary */}
                        {isResolved && (
                            <Paper p="lg" withBorder radius="md" bg="green.0">
                                <Group gap="xs" mb="xs">
                                    <ThemeIcon
                                        size="md"
                                        variant="filled"
                                        color="green"
                                        radius="xl"
                                    >
                                        <IconCheck size={16} />
                                    </ThemeIcon>
                                    <Text size="md" fw={600} c="green.8">
                                        {t('alert.status.resolved')}
                                    </Text>
                                </Group>
                                {alert.resolvedDate && (
                                    <Text size="sm" c="dimmed">
                                        {Utils.formatLocalDateToYYYYMMDD(alert.resolvedDate)}{' '}
                                        {Utils.formatLocalDateToTime(alert.resolvedDate)}
                                    </Text>
                                )}
                                {alert.patientNotifications && alert.patientNotifications.length > 0 && (
                                    <Group gap="xs" mt="sm">
                                        <ThemeIcon
                                            size="sm"
                                            variant="light"
                                            color="blue"
                                            radius="xl"
                                        >
                                            <IconBell size={12} />
                                        </ThemeIcon>
                                        <Text size="sm" c="blue">
                                            {t('alert.detail.patientNotified')} ({alert.patientNotifications.length})
                                        </Text>
                                    </Group>
                                )}
                            </Paper>
                        )}

                        {/* Audit Trail */}
                        <Paper p="lg" withBorder radius="md">
                            <Group justify="space-between" mb="md">
                                <Title order={5}>
                                    {t('alert.detail.auditTrail')}
                                </Title>
                                {alert.auditTrail && alert.auditTrail.length > 0 && (
                                    <Group gap="xs">
                                        <Button
                                            variant="subtle"
                                            size="compact-xs"
                                            onClick={() => setExpandedAuditIds(alert.auditTrail.map((e) => e.id))}
                                        >
                                            {t('alert.audit.expandAll')}
                                        </Button>
                                        <Button
                                            variant="subtle"
                                            size="compact-xs"
                                            onClick={() => setExpandedAuditIds([])}
                                        >
                                            {t('alert.audit.collapseAll')}
                                        </Button>
                                    </Group>
                                )}
                            </Group>
                            {alert.auditTrail && alert.auditTrail.length > 0 ? (
                                <Timeline
                                    active={alert.auditTrail.length - 1}
                                    bulletSize={24}
                                    lineWidth={2}
                                    color="gray"
                                >
                                    {alert.auditTrail.map((entry) => {
                                        const isExpanded = expandedAuditIds.includes(entry.id);
                                        return (
                                            <Timeline.Item
                                                key={entry.id}
                                                bullet={
                                                    <ThemeIcon
                                                        size={24}
                                                        radius="xl"
                                                        variant="filled"
                                                        color={auditActionColor(entry.action)}
                                                    >
                                                        {auditActionIcon(entry.action)}
                                                    </ThemeIcon>
                                                }
                                                title={
                                                    <UnstyledButton onClick={() =>
                                                        setExpandedAuditIds((prev) =>
                                                            isExpanded ? prev.filter((id) => id !== entry.id) : [...prev, entry.id]
                                                        )
                                                    }>
                                                        <Group gap={4}>
                                                            {isExpanded ? <IconChevronDown size={12} /> : <IconChevronRight size={12} />}
                                                            <Text size="sm" fw={500}>
                                                                {t(`alert.audit.${entry.action}`, { defaultValue: entry.action })}
                                                            </Text>
                                                        </Group>
                                                    </UnstyledButton>
                                                }
                                            >
                                                <Text size="xs" c="dimmed">
                                                    {Utils.formatLocalDateToYYYYMMDD(entry.eventDate)}{' '}
                                                    {Utils.formatLocalDateToTime(entry.eventDate)}
                                                    {' — '}
                                                    {entry.actorType === 'System'
                                                        ? t('alert.audit.bySystem')
                                                        : entry.actorName || t('alert.audit.byUser', { userId: entry.actorUserID })}
                                                </Text>
                                                <Collapse in={isExpanded}>
                                                    <Paper p="xs" bg="gray.0" radius="sm" mt={4}>
                                                        <AuditEntryDetail entry={entry} t={t} />
                                                    </Paper>
                                                </Collapse>
                                            </Timeline.Item>
                                        );
                                    })}
                                </Timeline>
                            ) : (
                                <Text c="dimmed" size="sm" ta="center" py="md">
                                    {t('alert.detail.noAuditTrail')}
                                </Text>
                            )}
                        </Paper>
                    </Stack>
                </Grid.Col>
            </Grid>
            {/* No Assistant Configured Modal */}
            <Modal
                opened={noAssistantModalOpen}
                onClose={() => setNoAssistantModalOpen(false)}
                title={t('alert.assistant.modalTitle')}
                centered
            >
                <Text size="sm" mb="lg">
                    {t('alert.assistant.noEmail')}
                </Text>
                <Group justify="flex-end">
                    <Button
                        variant="light"
                        onClick={() => setNoAssistantModalOpen(false)}
                    >
                        {t('button.dismiss')}
                    </Button>
                </Group>
            </Modal>
            {/* Send to Assistant Modal (shown after resolving) */}
            <Modal
                opened={sendToAssistantModalOpen}
                onClose={() => setSendToAssistantModalOpen(false)}
                title={t('alert.assistant.modalTitle')}
                centered
            >
                <Text size="sm" mb="lg">
                    {t('alert.assistant.modalMessage', {
                        name: alert.assistantName,
                        email: alert.assistantEmail,
                    })}
                </Text>
                <Group justify="flex-end" gap="sm">
                    <Button
                        variant="subtle"
                        color="gray"
                        onClick={() => setSendToAssistantModalOpen(false)}
                    >
                        {t('alert.assistant.skip')}
                    </Button>
                    <Button
                        color="grape"
                        leftSection={<IconMail size={16} />}
                        loading={sendToAssistant.isPending}
                        onClick={() => {
                            sendToAssistant.mutate(
                                { id: alert.id, language: i18n.language },
                                { onSuccess: () => setSendToAssistantModalOpen(false) },
                            );
                        }}
                    >
                        {t('alert.assistant.send')}
                    </Button>
                </Group>
            </Modal>
        </Stack>
    );
};

export default AlertDetailForm;
